/**
 * Geometry utilities for coordinate conversion and bounds checking
 */

import { HUD_ELEMENT_SELECTORS, EXCLUSION_ZONE_PADDING } from './constants';

/**
 * Convert screen coordinates to canvas coordinates
 * @param {number} screenX - X position in screen pixels
 * @param {number} screenY - Y position in screen pixels  
 * @param {{x: number, y: number}} pan - Current pan offset
 * @param {number} zoom - Current zoom level
 * @returns {{x: number, y: number}} Canvas coordinates
 */
export function screenToCanvas(screenX, screenY, pan, zoom) {
  return {
    x: (screenX - pan.x) / zoom,
    y: (screenY - pan.y) / zoom,
  };
}

/**
 * Get the viewport bounds in canvas coordinates
 * @param {DOMRect} containerRect - Container element's bounding rect
 * @param {{x: number, y: number}} pan - Current pan offset
 * @param {number} zoom - Current zoom level
 * @returns {Object|null} Viewport bounds in canvas coordinates
 */
export function getViewportBounds(containerRect, pan, zoom) {
  if (!containerRect) return null;
  
  const topLeft = screenToCanvas(0, 0, pan, zoom);
  const bottomRight = screenToCanvas(containerRect.width, containerRect.height, pan, zoom);
  
  return {
    x: topLeft.x,
    y: topLeft.y,
    width: bottomRight.x - topLeft.x,
    height: bottomRight.y - topLeft.y,
    right: bottomRight.x,
    bottom: bottomRight.y,
  };
}

/**
 * Get HUD exclusion zones by querying actual DOM elements
 * Returns their bounding boxes converted to canvas coordinates
 * 
 * @param {DOMRect} containerRect - Container element's bounding rect
 * @param {{x: number, y: number}} pan - Current pan offset
 * @param {number} zoom - Current zoom level
 * @returns {Array<Object>} Array of exclusion zone rectangles in canvas coordinates
 */
export function getExclusionZonesInCanvas(containerRect, pan, zoom) {
  if (!containerRect) return [];
  
  const zones = [];
  const pad = EXCLUSION_ZONE_PADDING;
  
  // Query each HUD element selector
  for (const selector of HUD_ELEMENT_SELECTORS) {
    const elements = document.querySelectorAll(selector);
    
    elements.forEach((element, index) => {
      const rect = element.getBoundingClientRect();
      
      // Skip elements that are hidden or have no size
      if (rect.width === 0 || rect.height === 0) return;
      
      // Get element position relative to the container
      // containerRect gives us the container's position on screen
      // We need to convert the element's screen position to be relative to container
      const relativeX = rect.left - containerRect.left;
      const relativeY = rect.top - containerRect.top;
      
      // Convert to canvas coordinates with padding
      const topLeft = screenToCanvas(
        relativeX - pad,
        relativeY - pad,
        pan,
        zoom
      );
      const bottomRight = screenToCanvas(
        relativeX + rect.width + pad,
        relativeY + rect.height + pad,
        pan,
        zoom
      );
      
      zones.push({
        name: `${selector.replace('.', '')}${index > 0 ? `-${index}` : ''}`,
        x: topLeft.x,
        y: topLeft.y,
        width: bottomRight.x - topLeft.x,
        height: bottomRight.y - topLeft.y,
      });
    });
  }
  
  return zones;
}

/**
 * Check if two rectangles overlap
 * @param {Object} rect1 - First rectangle {x, y, width, height}
 * @param {Object} rect2 - Second rectangle {x, y, width, height}
 * @returns {boolean} True if rectangles overlap
 */
export function rectanglesOverlap(rect1, rect2) {
  return (
    rect1.x < rect2.x + rect2.width &&
    rect1.x + rect1.width > rect2.x &&
    rect1.y < rect2.y + rect2.height &&
    rect1.y + rect1.height > rect2.y
  );
}

/**
 * Check if a rectangle overlaps with any exclusion zone
 * @param {Object} labelRect - Label rectangle {x, y, width, height}
 * @param {Array<Object>} exclusionZones - Array of exclusion zone rectangles
 * @returns {string|null} Name of overlapping zone, or null if no overlap
 */
export function getOverlappingExclusionZone(labelRect, exclusionZones) {
  for (const zone of exclusionZones) {
    if (rectanglesOverlap(labelRect, zone)) {
      return zone.name || 'unknown';
    }
  }
  return null;
}

/**
 * Calculate what percentage of a rectangle is visible within a viewport
 * @param {Object} rect - Rectangle {x, y, width, height}
 * @param {Object} viewport - Viewport bounds {x, y, right, bottom}
 * @returns {number} Ratio of visible area (0 to 1)
 */
export function calculateVisibleRatio(rect, viewport) {
  const visibleWidth = Math.min(rect.x + rect.width, viewport.right) - Math.max(rect.x, viewport.x);
  const visibleHeight = Math.min(rect.y + rect.height, viewport.bottom) - Math.max(rect.y, viewport.y);
  const visibleArea = Math.max(0, visibleWidth) * Math.max(0, visibleHeight);
  const totalArea = rect.width * rect.height;
  
  return totalArea > 0 ? visibleArea / totalArea : 0;
}

/**
 * Calculate distance between two points
 * @param {{x: number, y: number}} p1 - First point
 * @param {{x: number, y: number}} p2 - Second point
 * @returns {number} Euclidean distance
 */
export function pointDistance(p1, p2) {
  if (!p1 || !p2) return Infinity;
  const dx = p1.x - p2.x;
  const dy = p1.y - p2.y;
  return Math.sqrt(dx * dx + dy * dy);
}
